import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  input,
  output,
  inject,
  viewChild,
} from '@angular/core';
import {
  ColumnsModel,
  QueryBuilderComponent,
  QueryBuilderModule,
  RuleModel,
  TemplateColumn,
} from '@syncfusion/ej2-angular-querybuilder';
import {
  createElement,
  enableRipple,
  getComponent,
} from '@syncfusion/ej2-base';
import {
  DropDownList,
  DropDownListModule,
  MultiSelect,
  MultiSelectModule,
} from '@syncfusion/ej2-angular-dropdowns';
import {
  CheckBoxModule,
  RadioButtonModule,
} from '@syncfusion/ej2-angular-buttons';
import { Slider, SliderModule } from '@syncfusion/ej2-angular-inputs';
import {
  getNamedParameterSql,
  getParameterSql,
  updateRuleValue,
} from '@fitness-force/shared-utils';
import { AutomationmasterService } from '@fitness-force/api';
import { TranslateService } from '@ngx-translate/core';
import type {
  AutomationField,
  AutomationMaster,
  DataBind,
  Operator,
} from '@fitness-force/models';
import { BehaviorSubject, forkJoin, map, Observable, of } from 'rxjs';
import { RouteStateService } from '@fitness-force/shared-utils';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SliderModule,
    QueryBuilderModule,
    MultiSelectModule,
    DropDownListModule,
    RadioButtonModule,
    CheckBoxModule,
    SkeletonModule,
    AsyncPipe,
    NgClass,
  ],
})
export class AngularQueryBuilderComponent implements OnChanges {
  private automationService = inject(AutomationmasterService);
  private cd = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private stateService = inject(RouteStateService);

  readonly tenantId = input<number>(0);
  readonly eventType = input<string>();
  readonly query = input<string>('');
  readonly savedJsonRule = input<string>('');
  readonly isDisabled = input<boolean>(false);
  readonly rule = output<{
    rule: string;
    sqlRule: string;
    jsonRule: string;
  }>();
  readonly qryBldrObj = viewChild<QueryBuilderComponent>('querybuilder');

  automationMasters = new BehaviorSubject<AutomationMaster[]>([]);
  automationMaster = new BehaviorSubject<AutomationMaster>(
    {} as AutomationMaster,
  );
  values: string[] = ['Mr.', 'Mrs.'];
  content: string;
  ruleValue: string[] = [];
  queryType: string = 'namedParemeter';
  importRules = new BehaviorSubject<RuleModel>({});
  columns = new BehaviorSubject<ColumnsModel[]>([]);
  separator: string = '.';
  showQueryBuilder = new BehaviorSubject<boolean>(false);
  items: { [key: string]: Object }[] = [];
  sqlRule: string = '';
  jsonRule: string = '';

  constructor() {
    enableRipple(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (
      changes.eventType &&
      changes.eventType.currentValue != changes.eventType.previousValue
    ) {
      this.columns.next([]);
      this.importRules.next({});
      const qryBldrObj = this.qryBldrObj();
      if (qryBldrObj) {
        qryBldrObj.reset();
        qryBldrObj.setRules({ condition: 'and', rules: [] });
        //this.updateContent();
      }
      this.cd.detectChanges();
      this.getAutomationMaster();
    }

    if (
      changes.savedJsonRule &&
      changes.savedJsonRule.previousValue != undefined &&
      changes.savedJsonRule.currentValue != changes.savedJsonRule.previousValue
    ) {
      this.applySavedRules();
    }

    if (
      changes.query &&
      changes.query.previousValue != undefined &&
      changes.query.currentValue != changes.query.previousValue &&
      !this.savedJsonRule()?.trim()
    ) {
      this.tryRestoreFromSql(changes.query.currentValue);
    }
  }

  getAutomationMaster() {
    if (
      this.stateService.getAutomationMasters().length > 0 &&
      this.stateService.getAutomationEventType() == this.eventType() &&
      this.stateService.getAutomationTenantId() == this.tenantId()
    ) {
      this.HandleResponse(this.stateService.getAutomationMasters());
    } else {
      const eventType = this.eventType();
      if (!eventType) {
        return;
      }
      this.automationService
        .GetAutomationMaster(this.tenantId(), eventType.toString())
        .subscribe({
          next: (response: AutomationMaster[]) => {
            this.HandleResponse(response);
          },
          error: () => {
            console.error('Error fetching automation masters');
          },
        });
    }
  }

  HandleResponse(response: AutomationMaster[]) {
    this.automationMasters.next(response ? response : []);
    const matched = this.automationMasters.value.find(
      (o) => o.automation_type.toString() == this.eventType(),
    );
    if (!matched) {
      this.cd.detectChanges();
      return;
    }
    this.automationMaster.next(matched);
    this.stateService.setAutomationMasters(response);
    const eventType = this.eventType();
    const tenantId = this.tenantId();
    if (eventType) {
      this.stateService.setAutomationEventType(eventType);
    }
    if (tenantId) {
      this.stateService.setAutomationTenantId(tenantId);
    }
    forkJoin({
      columns: this.buildColumns$(),
      rules: this.buildImportRules$(),
    }).subscribe(({ columns, rules }) => {
      this.columns.next(columns);
      this.importRules.next(this.normalizeRules(rules));
      this.showQueryBuilder.next(true);
      this.cd.detectChanges();
      setTimeout(() => {
        const qryBldr = this.qryBldrObj();
        if (!qryBldr) {
          return;
        }

        qryBldr.reset();
        if (!this.applySavedRules()) {
          qryBldr.setRules(this.normalizeRules(this.importRules.value));
          this.tryRestoreFromSql(this.query());
        }
        this.cd.detectChanges();
      }, 300);
    });
  }

  private buildColumns$(): Observable<ColumnsModel[]> {
    const fields = this.automationMaster.value?.automation_fields;
    if (!fields?.length) {
      return of([]);
    }

    const sortedFields = [...fields].sort((a, b) =>
      a.isdefault === b.isdefault ? 0 : a.isdefault ? -1 : 1,
    );

    const columnObservables = sortedFields.map((field) =>
      this.translateService
        .get('AUTOMATION.FIELDS.' + field.field_name)
        .pipe(
          map((translation) => {
            const column: ColumnsModel = {} as ColumnsModel;
            const operators = this.getDistinctOperators(field.operators);
            if (field.data_bind?.length) {
              column.values = field.data_bind.map((item) => item.primaryName);
            }
            if (operators.length > 0) {
              column.operators = operators;
            }
            column.label = translation;
            column.field = field.field_name;
            column.type =
              field.column_type == 'datepicker' ? 'date' : field.column_type;
            column.template = this.setTemplate(field);
            return column;
          }),
        ),
    );

    return forkJoin(columnObservables);
  }

  private buildImportRules$(): Observable<RuleModel> {
    const fields = this.automationMaster.value?.automation_fields;
    if (!fields?.length) {
      return of({ condition: 'and', rules: [] });
    }

    const defaultRules = fields.filter(
      (field) => field.isdefault || field.ismandatory,
    );
    if (!defaultRules.length) {
      return of({ condition: 'and', rules: [] });
    }

    const ruleObservables = defaultRules.map((field) =>
      this.translateService
        .get('AUTOMATION.FIELDS.' + field.field_name)
        .pipe(
          map((translation) => {
            const operators = this.getDistinctOperators(field.operators);
            const operator =
              operators.length > 0
                ? String(operators[0].value).toLowerCase()
                : 'equal';
            const rule: RuleModel = {
              field: field.field_name,
              label: translation,
              type:
                field.column_type == 'datepicker' ? 'date' : field.column_type,
              operator,
              value: this.setDefaultValue(field, operator),
            };
            return rule;
          }),
        ),
    );

    return forkJoin(ruleObservables).pipe(
      map((rulesModels) => ({
        condition: 'and',
        rules: rulesModels,
      })),
    );
  }

  setDefaultValue(
    field: AutomationField,
    operator?: string,
  ): string | string[] | undefined {
    const operatorValue = (
      operator ??
      (this.getDistinctOperators(field.operators)[0]?.value as string) ??
      'equal'
    ).toLowerCase();
    const isListOperator = this.isListOperator(operatorValue);

    if (field.data_bind?.length) {
      const defaultValue = String(field.data_bind[0].id);
      return isListOperator ? [defaultValue] : defaultValue;
    }

    if (isListOperator) {
      if (field.column_type === 'number') {
        return ['0'];
      }
      if (field.column_type === 'bool') {
        return ['true'];
      }
      return [' '];
    }

    if (field.column_type === 'number') {
      return '0';
    }
    if (field.column_type === 'string') {
      return ' ';
    }
    if (field.column_type === 'bool') {
      return 'true';
    }

    return undefined;
  }

  private isListOperator(operator?: string): boolean {
    const operatorValue = operator?.toLowerCase() ?? '';
    return operatorValue === 'in' || operatorValue === 'notin';
  }

  private normalizeRuleValue(
    value: unknown,
    operator?: string,
  ): string | string[] | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (this.isListOperator(operator)) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item));
      }
      return [String(value)];
    }

    if (Array.isArray(value)) {
      return value.length > 0 ? String(value[0]) : undefined;
    }

    return String(value);
  }

  private normalizeRules(ruleModel: RuleModel): RuleModel {
    if (!ruleModel?.rules?.length) {
      return ruleModel ?? { condition: 'and', rules: [] };
    }

    return {
      ...ruleModel,
      rules: ruleModel.rules.map((item) => {
        if (item.rules?.length) {
          return this.normalizeRules(item);
        }

        return {
          ...item,
          operator: item.operator?.toLowerCase(),
          value: this.normalizeRuleValue(item.value, item.operator),
        };
      }),
    };
  }

  onQueryBuilderCreated(): void {
    setTimeout(() => this.updateContent());
  }

  change(args: any) {
    if (args.value === 'Inline') {
      this.queryType = 'inline';
    } else if (args.value === 'Parameterized') {
      this.queryType = 'parameterized';
    } else {
      this.queryType = 'namedParemeter';
    }
    this.updateContent();
  }

  updateContent(): void {
    const qryBldr = this.qryBldrObj();
    if (!qryBldr || this.columns.value.length === 0) {
      return;
    }

    try {
      const qbrule: RuleModel = qryBldr.getValidRules();
      this.jsonRule = JSON.stringify(qbrule);
      switch (this.queryType) {
        case 'inline':
          this.sqlRule = qryBldr.getSqlFromRules(qbrule);
          break;
        case 'parameterized':
          this.convertParameterSql(qbrule);
          break;
        default:
          this.convertNamedParameterSql(qbrule);
          break;
      }
    } catch (error) {
      console.error('Error updating query builder content:', error);
    }
  }

  setTemplate(automationField: AutomationField): any {
    switch (automationField.display_type) {
      case 'dropdown': {
        if (automationField.data_bind && automationField.data_bind.length > 0) {
          return this.createDropdownTemplate(automationField.data_bind);
        } else {
          return undefined;
        }
      }
      case 'multiselect': {
        if (automationField.data_bind && automationField.data_bind.length > 0) {
          return this.createMultiselectTemplate(automationField.data_bind);
        } else {
          return undefined;
        }
      }
      default:
        return undefined;
    }
  }

  convertParameterSql(qbrule: RuleModel): void {
    const qryBldr = this.qryBldrObj();
    if (!qryBldr) return;
    this.ruleValue = updateRuleValue(qbrule, false);
    this.sqlRule = qryBldr.getSqlFromRules(qbrule);
    this.content = getParameterSql(this.content, this.ruleValue);
    this.sqlRule = this.generateSQLQuery(this.content)!;
    this.rule.emit({
      rule: this.content,
      sqlRule: this.sqlRule,
      jsonRule: this.jsonRule,
    });
  }

  convertNamedParameterSql(qbrule: RuleModel): void {
    const qryBldr = this.qryBldrObj();
    if (!qryBldr) return;
    this.ruleValue = updateRuleValue(qbrule, true);
    this.content = qryBldr.getSqlFromRules(qbrule);
    this.sqlRule = qryBldr.getSqlFromRules(qbrule);
    this.content = getNamedParameterSql(this.content, this.ruleValue);
    this.sqlRule = this.generateSQLQuery(this.content)!;
    this.rule.emit({
      rule: this.content,
      sqlRule: this.sqlRule,
      jsonRule: this.jsonRule,
    });
  }

  createDropdownTemplate(value: DataBind[]): TemplateColumn {
    const fieldData: { [key: string]: Object }[] = [];
    const fields: Object = { text: 'key', value: 'value' };
    value.forEach((o) => {
      const idValue = String(o.id);
      fieldData.push({
        key: o.primaryName || o.secondaryName,
        value: idValue,
      });
    });

    const templateColumn: TemplateColumn = {
      create: () => {
        return createElement('input', { attrs: { type: 'text' } });
      },
      destroy: (args: { elementId: string }) => {
        const element = document.getElementById(args.elementId);
        if (!element) {
          return;
        }

        const multiSelect = getComponent(element, 'multiselect') as MultiSelect;
        if (multiSelect) {
          multiSelect.destroy();
        }

        const dropdown = getComponent(element, 'dropdownlist') as DropDownList;
        if (dropdown) {
          dropdown.destroy();
        }
      },
      write: (args: {
        elements: Element;
        values: string[] | string;
        operator: string;
      }) => {
        if (this.isListOperator(args.operator)) {
          const selectedValues = Array.isArray(args.values)
            ? args.values.map((value) => String(value))
            : args.values != null && args.values !== ''
              ? [String(args.values)]
              : [];

          const multiSelectObj = new MultiSelect({
            dataSource: fieldData,
            value: selectedValues,
            mode: 'CheckBox',
            placeholder: 'Select',
            fields: fields,
            change: (e: { value: string[]; element: Element }) => {
              this.qryBldrObj()?.notifyChange(e.value, e.element);
            },
          });
          multiSelectObj.appendTo('#' + args.elements.id);
          return;
        }

        const selectedValue = Array.isArray(args.values)
          ? String(args.values[0] ?? '')
          : args.values != null
            ? String(args.values)
            : undefined;

        const dropDownObj = new DropDownList({
          dataSource: fieldData,
          fields: fields,
          value: selectedValue,
          change: (e: { itemData: { value: string }; element: Element }) => {
            this.qryBldrObj()?.notifyChange(e.itemData.value, e.element);
          },
        });
        dropDownObj.appendTo('#' + args.elements.id);
      },
    };
    return templateColumn;
  }

  createMultiselectTemplate(value: DataBind[]) {
    const fieldData: { [key: string]: Object }[] = [];
    const fields: Object = { text: 'key', value: 'value' };
    value.forEach((o) => {
      fieldData.push({
        key: o.primaryName || o.secondaryName,
        value: String(o.id),
      });
    });
    const multiSelectTemplate = {
      create: () => {
        return createElement('input', { attrs: { type: 'text' } });
      },
      destroy: (args: { elementId: string }) => {
        const multiSelect: MultiSelect = getComponent(
          document.getElementById(args.elementId) as any,
          'multiselect',
        ) as MultiSelect;
        if (multiSelect) {
          multiSelect.destroy();
        }
        const dropdown: DropDownList = getComponent(
          document.getElementById(args.elementId) as any,
          'dropdownlist',
        ) as DropDownList;
        if (dropdown) {
          dropdown.destroy();
        }
      },
      write: (args: {
        elements: Element;
        values: string[] | string;
        operator: string;
      }) => {
        if (this.isListOperator(args.operator)) {
          const selectedValues = Array.isArray(args.values)
            ? args.values.map((value) => String(value))
            : args.values != null && args.values !== ''
              ? [String(args.values)]
              : [];

          const multiSelectObj = new MultiSelect({
            dataSource: fieldData,
            value: selectedValues,
            mode: 'CheckBox',
            placeholder: 'Select',
            fields: fields,
            change: (e: { value: string[]; element: Element }) => {
              this.qryBldrObj()?.notifyChange(e.value, e.element);
            },
          });
          multiSelectObj.appendTo('#' + args.elements.id);
          return;
        }

        const selectedValue = Array.isArray(args.values)
          ? String(args.values[0] ?? '')
          : args.values != null
            ? String(args.values)
            : undefined;

        const dropDownObj = new DropDownList({
          dataSource: fieldData,
          value: selectedValue,
          fields: fields,
          change: (e: { itemData: { value: string }; element: Element }) => {
            this.qryBldrObj()?.notifyChange(e.itemData.value, e.element);
          },
        });
        dropDownObj.appendTo('#' + args.elements.id);
      },
    };
    return multiSelectTemplate;
  }

  createAmountTemaplte() {
    const amountTemplate: TemplateColumn = {
      create: () => {
        return createElement('div', { className: 'ticks_slider' });
      },
      destroy: (args: { elementId: string }) => {
        (
          getComponent(
            document.getElementById(args.elementId)!,
            'slider',
          ) as Slider
        ).destroy();
      },
      write: (args: { elements: Element; values: number }) => {
        const ticksSlider: Slider = new Slider({
          value: args.values,
          min: 0,
          max: 100,
          type: 'MinRange',
          // Initialize tooltip with placement and showOn
          tooltip: { isVisible: true, placement: 'Before', showOn: 'Hover' },
          change: (e: any) => {
            if (e.isInteracted) {
              this.qryBldrObj()?.notifyChange(e.value, args.elements);
            }
          },
        });
        ticksSlider.appendTo('#' + args.elements.id);
      },
    };
  }

  getDistinctOperators(operators: Operator[] | undefined) {
    if (!operators?.length) {
      return [];
    }

    const uniqueOperators = new Set();
    // Filter the array to get distinct records based on both value and key
    const distinctOperators = operators.filter((item) => {
      const key = JSON.stringify(item);
      if (!uniqueOperators.has(key)) {
        uniqueOperators.add(key);
        return true;
      }
      return false;
    });
    const fieldData: { [key: string]: Object }[] = [];
    distinctOperators.forEach((o) => {
      fieldData.push({ key: o.key, value: String(o.value).toLowerCase() });
    });
    return fieldData;
  }

  getfieldValues(values: DataBind[]) {
    return values.map((o) => o.id);
  }

  generateSQLQuery(rule: string): string {
    try {
      const ruleJson = JSON.parse(rule);
      const sqlRule = ruleJson.sql;
      const params = ruleJson.params || {};
      const dynamicSqlRule = sqlRule.replace(
        /:\w+/g,
        (placeholderKey: string) => {
          const key = placeholderKey.substring(1); // Remove ":" from the key
          return params[key]; // Replace placeholder with corresponding value
        },
      );
      return dynamicSqlRule;
    } catch (error) {
      console.error('Error:', error);
      return '';
    }
  }

  private applySavedRules(): boolean {
    const savedJsonRule = this.savedJsonRule()?.trim();
    if (!savedJsonRule) {
      return false;
    }

    const qryBldr = this.qryBldrObj();
    if (!qryBldr) {
      return false;
    }

    try {
      const parsedRules = JSON.parse(savedJsonRule) as RuleModel;
      if (!parsedRules?.rules?.length) {
        return false;
      }

      qryBldr.setRules(this.normalizeRules(parsedRules));
      this.updateContent();
      return true;
    } catch (error) {
      console.error('Error restoring saved query builder rules:', error);
      return false;
    }
  }

  private tryRestoreFromSql(query?: string): void {
    const queryValue = (query ?? this.query())?.trim();
    if (!queryValue) {
      return;
    }

    const qryBldr = this.qryBldrObj();
    if (!qryBldr) {
      return;
    }

    try {
      let sqlToParse = queryValue;

      try {
        const parsedQuery = JSON.parse(queryValue) as { sql?: string };
        if (typeof parsedQuery.sql === 'string') {
          sqlToParse = parsedQuery.sql;
        }
      } catch {
        // Not JSON — treat as plain SQL.
      }

      if (sqlToParse.startsWith('(') && sqlToParse.endsWith(')')) {
        sqlToParse = sqlToParse.slice(1, -1);
      }

      const cleanQuery = sqlToParse.replace(/\s+/g, ' ').trim();
      if (!cleanQuery) {
        return;
      }

      qryBldr.setRulesFromSql(cleanQuery);
      const currentRules = qryBldr.getValidRules();

      if (currentRules?.rules) {
        qryBldr.setRules(this.normalizeRules(currentRules));
      }

      this.updateContent();
    } catch (error) {
      console.error('Error setting rules from SQL:', error);
    }
  }

  setQuery(query: string) {
    if (this.applySavedRules()) {
      return;
    }

    this.tryRestoreFromSql(query);
  }
}
